import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  Textarea,
  Badge,
} from '../../../components';
import { OrganizationProfile } from '../types';
import {
  INDUSTRY_OPTIONS,
  ORGANIZATION_SIZE_OPTIONS,
  TIMEZONE_OPTIONS,
  COUNTRY_OPTIONS,
} from '../constants';

interface OrganizationProfileProps {
  profile: OrganizationProfile;
  isEditing: boolean;
  isSaving: boolean;
  fieldErrors: Record<string, string>;
  onUpdateProfile: (updates: Partial<OrganizationProfile>) => void;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onSaveProfile: () => Promise<{ success: boolean; error?: string }>;
}

export const OrganizationProfileComponent: React.FC<OrganizationProfileProps> = ({
  profile,
  isEditing,
  isSaving,
  fieldErrors,
  onUpdateProfile,
  onStartEditing,
  onCancelEditing,
  onSaveProfile,
}) => {
  const { t } = useTranslation();

  const handleSave = async () => {
    await onSaveProfile();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t('organization.profile.title')}</CardTitle>
          {!isEditing && (
            <Button onClick={onStartEditing} variant="gradient-multi">
              <span className="material-icons mr-2">edit</span>
              {t('organization.profile.editProfile')}
            </Button>
          )}
          {isEditing && (
            <div className="flex gap-2">
              <Button onClick={onCancelEditing} variant="secondary">
                <span className="material-icons mr-2">close</span>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} variant="gradient-multi" disabled={isSaving}>
                <span className="material-icons mr-2">save</span>
                {isSaving ? t('common.saving') : t('common.saveChanges')}
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Company Information */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b-2 border-primary-500 flex items-center">
                <span className="material-icons mr-2 text-primary-500">business</span>
                {t('organization.profile.companyInformation')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="org-name"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('organization.profile.organizationName')} *
                  </label>
                  {isEditing ? (
                    <Input
                      id="org-name"
                      value={profile.name || ''}
                      onChange={e => onUpdateProfile({ name: e.target.value })}
                      className="w-full"
                      required
                      errorText={fieldErrors.name}
                    />
                  ) : (
                    <div className="text-gray-900 dark:text-white">{profile.name}</div>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="org-industry"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('organization.profile.industry')}
                  </label>
                  {isEditing ? (
                    <Select
                      id="org-industry"
                      value={profile.industry}
                      onChange={value => onUpdateProfile({ industry: value as string })}
                      options={INDUSTRY_OPTIONS}
                      className="w-full"
                      error={!!fieldErrors.industry}
                      errorText={fieldErrors.industry}
                    />
                  ) : (
                    <div className="text-gray-900 dark:text-white">{profile.industry}</div>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="org-company-size"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('organization.profile.companySize')}
                  </label>
                  {isEditing ? (
                    <Select
                      id="org-company-size"
                      value={profile.size}
                      onChange={value => onUpdateProfile({ size: value as string })}
                      options={ORGANIZATION_SIZE_OPTIONS}
                      className="w-full"
                      error={!!fieldErrors.size}
                      errorText={fieldErrors.size}
                    />
                  ) : (
                    <div className="text-gray-900 dark:text-white">{profile.size_display}</div>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="org-vat-id"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('organization.profile.vatId')}
                  </label>
                  {isEditing ? (
                    <Input
                      id="org-vat-id"
                      value={profile.vat_id || ''}
                      onChange={e => onUpdateProfile({ vat_id: e.target.value })}
                      className="w-full"
                      placeholder={t('organization.profile.enterVatId')}
                      errorText={fieldErrors.vat_id}
                    />
                  ) : (
                    <div className="text-gray-900 dark:text-white">{profile.vat_id}</div>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="org-tax-id"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('organization.profile.taxId')}
                  </label>
                  {isEditing ? (
                    <Input
                      id="org-tax-id"
                      value={profile.tax_id || ''}
                      onChange={e => onUpdateProfile({ tax_id: e.target.value })}
                      className="w-full"
                      placeholder={t('organization.profile.enterTaxId')}
                      errorText={fieldErrors.tax_id}
                    />
                  ) : (
                    <div className="text-gray-900 dark:text-white">{profile.tax_id}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b-2 border-primary-500 flex items-center">
                <span className="material-icons mr-2 text-primary-500">contact_mail</span>
                {t('organization.profile.contactDetails')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="org-contact-email"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('organization.profile.contactEmail')} *
                  </label>
                  {isEditing ? (
                    <Input
                      id="org-contact-email"
                      type="email"
                      value={profile.contact_email || ''}
                      onChange={e => onUpdateProfile({ contact_email: e.target.value })}
                      className="w-full"
                      required
                      errorText={fieldErrors.contact_email}
                    />
                  ) : (
                    <div className="text-gray-900 dark:text-white">{profile.contact_email}</div>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="org-phone"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('organization.profile.phoneNumber')}
                  </label>
                  {isEditing ? (
                    <Input
                      id="org-phone"
                      type="tel"
                      value={profile.contact_phone || ''}
                      onChange={e => onUpdateProfile({ contact_phone: e.target.value })}
                      className="w-full"
                      errorText={fieldErrors.contact_phone}
                    />
                  ) : (
                    <div className="text-gray-900 dark:text-white">{profile.contact_phone}</div>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="org-address"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('organization.profile.address')}
                  </label>
                  {isEditing ? (
                    <Textarea
                      id="org-address"
                      value={profile.address || ''}
                      onChange={e => onUpdateProfile({ address: e.target.value })}
                      rows={3}
                      className="w-full"
                      errorText={fieldErrors.address}
                    />
                  ) : (
                    <div className="text-gray-900 dark:text-white whitespace-pre-line">
                      {profile.address}
                    </div>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="org-country"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('auth.country')} *
                  </label>
                  {isEditing ? (
                    <Select
                      id="org-country"
                      value={profile.country || ''}
                      onChange={value => onUpdateProfile({ country: value as string })}
                      options={COUNTRY_OPTIONS}
                      className="w-full"
                      required
                      error={!!fieldErrors.country}
                      errorText={fieldErrors.country}
                    />
                  ) : (
                    <div className="text-gray-900 dark:text-white">
                      {profile.country || 'Not set'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b-2 border-primary-500 flex items-center">
                <span className="material-icons mr-2 text-primary-500">credit_card</span>
                {t('organization.profile.billingInformation')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="org-billing-email"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('organization.profile.billingEmail')}
                  </label>
                  {isEditing ? (
                    <Input
                      id="org-billing-email"
                      type="email"
                      value={profile.billing_email || ''}
                      onChange={e => onUpdateProfile({ billing_email: e.target.value })}
                      className="w-full"
                      errorText={fieldErrors.billing_email}
                    />
                  ) : (
                    <div className="text-gray-900 dark:text-white">{profile.billing_email}</div>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="org-billing-address"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('organization.profile.billingAddress')}
                  </label>
                  {isEditing ? (
                    <Textarea
                      id="org-billing-address"
                      value={profile.billing_address || ''}
                      onChange={e => onUpdateProfile({ billing_address: e.target.value })}
                      rows={3}
                      className="w-full"
                      errorText={fieldErrors.billing_address}
                    />
                  ) : (
                    <div className="text-gray-900 dark:text-white whitespace-pre-line">
                      {profile.billing_address}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Account Status & Preferences */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 pb-2 border-b-2 border-primary-500 flex items-center">
                <span className="material-icons mr-2 text-primary-500">verified</span>
                {t('organization.profile.accountStatus')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="org-status"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('organization.profile.status')}
                  </label>
                  <Badge
                    variant={
                      profile.status === 'active'
                        ? 'success'
                        : profile.status === 'inactive'
                          ? 'warning'
                          : 'error'
                    }
                  >
                    {profile.status_display}
                  </Badge>
                </div>
                <div>
                  <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('organization.profile.created')}
                  </div>
                  <div className="text-gray-900 dark:text-white">
                    {new Date(profile.created).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </div>
                </div>
                <div>
                  <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('organization.profile.lastUpdated')}
                  </div>
                  <div className="text-gray-900 dark:text-white">
                    {new Date(profile.modified).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </div>
                </div>
                <div>
                  <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('organization.profile.owner')}
                  </div>
                  <div className="text-gray-900 dark:text-white">
                    {profile.owner ? (
                      <>
                        {profile.owner.first_name} {profile.owner.last_name} ({profile.owner.email})
                      </>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">N/A</span>
                    )}
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="org-timezone"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {t('organization.profile.timezone') || 'Timezone'}
                  </label>
                  {isEditing ? (
                    <Select
                      id="org-timezone"
                      value={profile.timezone || ''}
                      onChange={value => onUpdateProfile({ timezone: value as string })}
                      options={TIMEZONE_OPTIONS}
                      className="w-full"
                      error={!!fieldErrors.timezone}
                      errorText={fieldErrors.timezone}
                    />
                  ) : (
                    <div className="text-gray-900 dark:text-white">
                      {profile.timezone
                        ? TIMEZONE_OPTIONS.find(tz => tz.value === profile.timezone)?.label ||
                          profile.timezone
                        : 'Not set'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
